import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Eye } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { useNewCount } from "../NewCountContext";

const OMR = {
  "boern-unge-familier": "Børn, unge og familier",
  "voksne-social-stabilisering": "Voksne og social stabilisering",
  "beskaeftigelse-fastholdelse": "Beskæftigelse og fastholdelse",
  "loesladelse-resocialisering": "Løsladelse og resocialisering",
  andet: "Andet", "": "Ikke angivet",
};
const INTERNAL = { new: "Ny", in_progress: "I gang", waiting: "Afventer", closed: "Afsluttet" };
const fmtDate = (s) => (s ? new Date(s).toLocaleString("da-DK", { dateStyle: "long", timeStyle: "short" }) : "—");

const Field = ({ label, children, testid }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-[#5C7567] mb-1">{label}</p>
    <p className="text-[#1A2E24]" data-testid={testid}>{children || "—"}</p>
  </div>
);

const MessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh: refreshNewCount } = useNewCount();
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("new");
  const [assigned, setAssigned] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.message(id)
      .then((d) => {
        setMsg(d);
        setStatus(d.internal_status || "new");
        setAssigned(d.assigned_to || "");
        setNotes(d.notes || "");
      })
      .catch((err) => setError(formatApiErrorDetail(err.detail) || "Kunne ikke hente henvendelsen."));
  }, [id]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.patchMessage(id, { internal_status: status, assigned_to: assigned, notes });
      setSaved(true);
      refreshNewCount();
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.detail) || "Kunne ikke gemme.");
    } finally {
      setSaving(false);
    }
  };

  if (error) return <p className="text-red-600" data-testid="admin-detail-error">{error}</p>;
  if (!msg) return <div className="flex items-center gap-2 text-[#5C7567]"><Loader2 className="h-5 w-5 animate-spin" /> Henter…</div>;

  return (
    <div data-testid="admin-detail-page" className="max-w-3xl">
      <button onClick={() => navigate("/admin/indbakke")} data-testid="admin-detail-back"
        className="inline-flex items-center gap-1.5 text-sm text-[#5C7567] hover:text-[#1A2E24] mb-5">
        <ArrowLeft className="h-4 w-4" /> Tilbage til indbakke
      </button>

      <div className="rounded-2xl bg-[#2D5A3F]/5 border border-[#2D5A3F]/10 px-4 py-3 mb-6 flex items-center gap-2 text-sm text-[#2D5A3F]">
        <Eye className="h-4 w-4" /> Denne visning indeholder personoplysninger og er registreret i aktivitetsloggen.
      </div>

      <h1 className="font-display text-3xl text-[#1A2E24] mb-1">Henvendelse</h1>
      <p className="text-sm text-[#5C7567] mb-6">Modtaget {fmtDate(msg.created_at)}</p>

      <div className="rounded-2xl bg-white border border-black/5 p-6 grid sm:grid-cols-2 gap-5 mb-6">
        <Field label="Navn" testid="admin-detail-name">{msg.name}</Field>
        <Field label="E-mail" testid="admin-detail-email">{msg.email}</Field>
        <Field label="Telefon" testid="admin-detail-phone">{msg.phone}</Field>
        <Field label="Organisation">{msg.organization}</Field>
        <Field label="Indsatsområde">{OMR[msg.indsatsomraade] ?? msg.indsatsomraade}</Field>
        <Field label="Forløb">{msg.forloeb}</Field>
        <Field label="Henvendelse drejer sig om">{msg.henvendelse}</Field>
        <Field label="Leveringsstatus">{msg.delivery_status}</Field>
      </div>

      <div className="rounded-2xl bg-white border border-black/5 p-6 mb-6">
        <p className="text-xs uppercase tracking-wide text-[#5C7567] mb-2">Besked</p>
        <p className="text-[#1A2E24] whitespace-pre-wrap leading-relaxed" data-testid="admin-detail-message">{msg.message}</p>
      </div>

      <div className="rounded-2xl bg-white border border-black/5 p-6">
        <h2 className="font-display text-xl text-[#1A2E24] mb-4">Intern håndtering</h2>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#5C7567] mb-1.5">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-detail-status"
              className="w-full rounded-xl border border-black/10 px-3 py-2.5 bg-white">
              {Object.entries(INTERNAL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#5C7567] mb-1.5">Tildelt til</label>
            <input value={assigned} onChange={(e) => setAssigned(e.target.value)} data-testid="admin-detail-assigned"
              placeholder="Navn eller e-mail"
              className="w-full rounded-xl border border-black/10 px-3 py-2.5" />
          </div>
        </div>
        <label className="block text-xs uppercase tracking-wide text-[#5C7567] mb-1.5">Interne noter</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="admin-detail-notes"
          className="w-full rounded-xl border border-black/10 px-3 py-2.5 mb-4" />
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} data-testid="admin-detail-save"
            className="inline-flex items-center gap-2 rounded-full bg-[#2D5A3F] text-white px-5 py-2.5 text-sm hover:bg-[#244b34] transition-colors duration-200 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Gem ændringer
          </button>
          {saved && <span className="text-sm text-green-700" data-testid="admin-detail-saved">Gemt ✓</span>}
        </div>
      </div>
    </div>
  );
};

export default MessageDetail;
